import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  Stack,
  Switch,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import { createModeRequiredNumberSchema } from "@/pages/workflows/edit/actions/workflow-edit-action-validation.utils.ts";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";
import { useDisqualifications } from "@/queries/recruit/use-disqualifications.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import { useWorkflowUpdateBlockActionCandidateStageChange } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiWorkflowBlockActionCandidateStageChange,
  ApiWorkflowBlockActionCandidateStageChangeMode,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface WorkflowEditActionCandidateStageModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionCandidateStageChange: ApiWorkflowBlockActionCandidateStageChange;
}

interface CandidateStageFormValues extends WorkflowActionTimingFormFields {
  title: string | null;
  mode: ApiWorkflowBlockActionCandidateStageChangeMode;
  stage_id: number | null;
  disqualification_reason_id: number | null;
  reserved: boolean;
  comment: string | null;
}

export const WorkflowEditActionCandidateStageModal = ({
  workflowVersion,
  block,
  actionCandidateStageChange,
  ...props
}: WorkflowEditActionCandidateStageModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();

  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    mode: yup
      .mixed<ApiWorkflowBlockActionCandidateStageChangeMode>()
      .oneOf(Object.values(ApiWorkflowBlockActionCandidateStageChangeMode))
      .required(t("errors:field_required")),
    stage_id: createModeRequiredNumberSchema(
      ApiWorkflowBlockActionCandidateStageChangeMode.change_stage,
      t("errors:field_required")
    ),
    disqualification_reason_id: createModeRequiredNumberSchema(
      ApiWorkflowBlockActionCandidateStageChangeMode.disqualify,
      t("errors:field_required")
    ),
    reserved: yup.boolean().defined(),
    comment: yup.string().defined().nullable(),
  });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<CandidateStageFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      ...getWorkflowActionTimingValues(block),
      title: block.title ?? "",
      mode: actionCandidateStageChange.mode,
      stage_id: actionCandidateStageChange.stage_id,
      disqualification_reason_id: actionCandidateStageChange.disqualification_reason_id,
      reserved: actionCandidateStageChange.reserved,
      comment: actionCandidateStageChange.comment ?? "",
    },
  });

  const { data: stages, isLoading: isStagesLoading } = useStages();
  const { data: disqualificationReasons, isLoading: isReasonsLoading } = useDisqualifications();

  const updateBlockAction = useWorkflowUpdateBlockActionCandidateStageChange(control);

  const mode = watch("mode");
  const isDisqualify = mode === ApiWorkflowBlockActionCandidateStageChangeMode.disqualify;

  const handleSave = handleSubmit(async (data) => {
    try {
      const comment = data.comment?.trim() ? data.comment : null;
      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: {
            mode: data.mode,
            stage_id: isDisqualify ? null : data.stage_id,
            disqualification_reason_id: isDisqualify ? data.disqualification_reason_id : null,
            reserved: isDisqualify ? data.reserved : false,
            comment,
          },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.candidate_stage_change,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const [activeTab, setActiveTab] = useState(0);

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_candidate_stage_change")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={handleSave}
            isLoading={updateBlockAction.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("common:settings")}>
            <Stack gap={"xs"} className={"py-6"}>
              <Controller
                control={control}
                name={"mode"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("workflow:action_type_candidate_stage_change_mode")}
                    emptyTitle={t("common:select")}
                    value={value}
                    required={true}
                    options={[
                      {
                        id: ApiWorkflowBlockActionCandidateStageChangeMode.change_stage,
                        title: t("workflow:action_type_candidate_stage_change_mode_change_stage"),
                      },
                      {
                        id: ApiWorkflowBlockActionCandidateStageChangeMode.disqualify,
                        title: t("workflow:action_type_candidate_stage_change_mode_disqualify"),
                      },
                    ]}
                    onChange={onChange}
                    error={errors.mode?.message}
                  />
                )}
              />

              {!isDisqualify && (
                <Controller
                  control={control}
                  name={"stage_id"}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("ats:stage")}
                      emptyTitle={t("common:select")}
                      isLoading={isStagesLoading}
                      value={value}
                      required={true}
                      options={(stages ?? []).map((stage) => ({
                        id: stage.id,
                        title: stage.title,
                        emoji: stage.emoji,
                      }))}
                      onChange={(selected) => onChange(selected as number | null)}
                      error={errors.stage_id?.message}
                    />
                  )}
                />
              )}

              {isDisqualify && (
                <>
                  <Controller
                    control={control}
                    name={"disqualification_reason_id"}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={t("ats:disqualification_reason")}
                        emptyTitle={t("common:select")}
                        isLoading={isReasonsLoading}
                        value={value}
                        required={true}
                        options={(disqualificationReasons ?? []).map((reason) => ({
                          id: reason.id,
                          title: reason.title,
                        }))}
                        onChange={(selected) => onChange(selected as number | null)}
                        error={errors.disqualification_reason_id?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={"reserved"}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        className="my-(--gap-md)"
                        title={t("ats:reserved")}
                        checked={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </>
              )}

              <Textarea
                label={t("common:comment")}
                {...register("comment")}
                error={errors.comment?.message}
              />
            </Stack>
          </TabItem>
          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("title")}
                  error={errors.title?.message}
                />
              </Stack>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />
              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
